import React, { useCallback, useLayoutEffect, useState } from 'react';
import Animated from 'react-native-reanimated';
import { StyleSheet, View } from 'react-native';
import { addYears, formatDate } from 'date-fns';
import Clipboard from '@react-native-clipboard/clipboard';
import { AppView, rem, SCREEN_BOTTOM_PADDING, SettingsItem, SettingsSection, withAnimatedNavigationHeader, } from 'design-system';
import { SettingsItemRightContent } from 'design-system/SettingsItem';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useTimeout } from 'hooks';
import { useAppSelector } from 'store/hooks';
import InfoCard from 'design-system/InfoCard';
import Text from 'design-system/Text';
import IconCopy from 'design-system/icons/IconCopy';
import { IconBoldCalendar, IconDocument, IconLocation, IconSearch, IconUser, } from 'design-system/icons';
import useColors from 'hooks/useColors';
import ReferenceCopiedToast from 'design-system/screens/ReferenceCopiedToast';
import TouchableBounce from 'utils/packages/TouchableBounce';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { selectCreditReportSearchByReference, selectIsSoftCreditReportSearch, } from '../selectors';
import useCreditReportDetailItem from '../hooks/useCreditReportDetailItem';
import { isAndroid, isWeb } from '../../../constants';
import CreditReportLenderLogo from '../components/CreditReportLenderLogo';
import DisputeActionCard from '../components/DisputeActionCard';
const strings = {
    fallbackTitle: 'Search detail',
    rows: {
        date: 'Date',
        purpose: 'Purpose',
        reference: 'Reference',
        name: 'Name',
        dateOfBirth: 'Date of birth',
        address: 'Address',
    },
    yourDetailsHeader: 'The company used these details to search for your credit report',
};
const formatItemDate = (value) => {
    if (!value)
        return null;
    try {
        return formatDate(new Date(value), 'd MMM yyyy');
    }
    catch {
        return value;
    }
};
const CreditReportSearchDetailScreen = withAnimatedNavigationHeader(({ scrollHandler, route, navigation }) => {
    const { searchReference } = route.params;
    const isSoft = useAppSelector((state) => selectIsSoftCreditReportSearch(state, searchReference));
    const goToParent = useCallback(() => navigation.navigate('CreditReportSearchScreen', { isSoft }), [navigation, isSoft]);
    const search = useCreditReportDetailItem(!!searchReference, (state) => selectCreditReportSearchByReference(state, searchReference), goToParent);
    const colors = useColors();
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, [navigation]);
    const untilDateString = !isSoft && search?.searchDate
        ? formatDate(addYears(new Date(search.searchDate), 2), 'd MMM yyyy')
        : null;
    const toolTipCopy = isSoft
        ? 'This will not affect your credit score.'
        : `Organisations can see this search on your credit report${untilDateString ? ` until ${untilDateString}` : ''}.`;
    const searchDate = formatItemDate(search?.searchDate ?? null);
    const dateOfBirth = formatItemDate(search?.dateOfBirth ?? null);
    const addressLine = search?.address ?? null;
    const [copyToastVisible, setCopyToastVisible] = useState(false);
    useTimeout(() => setCopyToastVisible(false), copyToastVisible ? 4000 : undefined);
    const copyReference = useCallback(() => {
        const reference = search?.searchReference;
        if (!reference)
            return;
        Clipboard.setString(reference);
        if (!isAndroid) {
            setCopyToastVisible(true);
        }
    }, [search?.searchReference]);
    if (!search) {
        return <AppView withFrame={false}/>;
    }
    return (<AppView withFrame={false}>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} showsVerticalScrollIndicator contentContainerStyle={contentContainerStyle}>
            <View style={styles.headerContainer}>
              <CreditReportLenderLogo size="large" onDark iconUrl={search.iconUrl} style={styles.icon} name={search.company}/>
              <View style={styles.headerTextContainer}>
                <Text Text-20>{search.company}</Text>
                <Text TextThin-16 Gray>
                  {isSoft ? 'Soft search' : 'Hard search'}
                </Text>
              </View>
            </View>

            <InfoCard style={styles.infoCardMargin}>
              <Text TextThin-14 Gray>
                {toolTipCopy}
              </Text>
            </InfoCard>

            <SettingsSection noMarginTop>
              {!!searchDate && (<SettingsItem title={searchDate} topDescription={strings.rows.date} icon={<IconBoldCalendar gradientKey="apricot"/>}/>)}
              {!!search.searchPurpose && (<SettingsItem title={search.searchPurpose} topDescription={strings.rows.purpose} icon={<IconSearch alt gradientKey="skyBlue"/>}/>)}
              {!!search.searchReference && (<SettingsItem title={search.searchReference.replaceAll(/[{}]/g, '')} topDescription={strings.rows.reference} icon={<IconDocument gradientKey="orange"/>} disabled onPress={copyReference}>
                  <SettingsItemRightContent style={styles.copyRef}>
                    <TouchableBounce onPress={copyReference}>
                      <IconCopy fill={colors.elements.secondary}/>
                    </TouchableBounce>
                  </SettingsItemRightContent>
                </SettingsItem>)}
            </SettingsSection>

            <SettingsSection name="Your details">
              <Text TextThin-14 Secondary style={styles.yourDetailsHeader}>
                {strings.yourDetailsHeader}
              </Text>
              {!!search.name && (<SettingsItem title={search.name} topDescription={strings.rows.name} icon={<IconUser gradientKey="darkPurple"/>}/>)}
              {!!dateOfBirth && (<SettingsItem title={dateOfBirth} topDescription={strings.rows.dateOfBirth} icon={<IconBoldCalendar gradientKey="skyBlue"/>}/>)}
              {!!addressLine && (<SettingsItem title={addressLine} topDescription={strings.rows.address} icon={<IconLocation alt gradientKey="yellow"/>}/>)}
            </SettingsSection>

            <DisputeActionCard params={{
            category: 'search',
            searchReference: search.searchReference ?? undefined,
        }}/>
          </Animated.ScrollView>
          <ReferenceCopiedToast isVisible={copyToastVisible} onClose={() => setCopyToastVisible(false)} withPaddingHorizontal bottom={'10%'}/>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
export default CreditReportSearchDetailScreen;
const styles = StyleSheet.create({
    headerContainer: {
        paddingHorizontal: rem(16),
        flexDirection: 'row',
        gap: rem(16),
        paddingTop: rem(8),
    },
    headerTextContainer: {
        flex: 1,
        gap: rem(4),
        marginTop: rem(6),
    },
    infoCardMargin: {
        marginTop: rem(16),
        marginBottom: rem(16),
    },
    icon: {
        alignSelf: 'flex-start',
    },
    yourDetailsHeader: {
        padding: rem(16),
    },
    copyRef: {
        marginLeft: rem(16),
    },
});
